import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaEnvelope, FaPhone, FaUserTie, FaCheckCircle, FaStar, FaAward, FaCalendarAlt, FaList, FaBriefcase } from 'react-icons/fa';
import '../pages_premium.css';

function HiremateCandidateProfileView() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [candidate, setCandidate] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchCandidate = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/admin/hiremates/${id}`);
            if (res.data) {
                setCandidate(res.data);
            }
        } catch (error) {
            console.error('Error fetching candidate profile:', error);
            toast.error('Failed to load candidate profile details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCandidate();
    }, [id]);

    const parseJSON = (val, fallback = []) => {
        if (!val) return fallback;
        if (typeof val === 'object') return val;
        try {
            return JSON.parse(val);
        } catch (e) {
            return fallback;
        }
    };

    const renderBadges = (val, colorScheme = 'blue') => {
        const parsed = parseJSON(val, Array.isArray(val) ? val : (typeof val === 'string' && val ? [val] : []));
        const list = Array.isArray(parsed) ? parsed : (typeof parsed === 'object' && parsed ? Object.keys(parsed) : [String(parsed)]);
        
        if (!list || list.length === 0) return <span style={{ color: '#94a3b8', fontSize: '13px' }}>-</span>;

        const bg = colorScheme === 'blue' ? '#e0f2fe' : (colorScheme === 'green' ? '#dcfce7' : '#f3e8ff');
        const color = colorScheme === 'blue' ? '#0369a1' : (colorScheme === 'green' ? '#15803d' : '#6b21a8');

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {list.map((item, idx) => (
                    <span key={idx} style={{
                        backgroundColor: bg,
                        color: color,
                        fontSize: '12px',
                        fontWeight: '600',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        whiteSpace: 'nowrap'
                    }}>
                        {typeof item === 'object' ? (item.title || item.name || JSON.stringify(item)) : String(item)}
                    </span>
                ))}
            </div>
        );
    };

    const renderExperienceSummary = (exp) => {
        const expObj = parseJSON(exp, exp);
        if (!expObj) return <span style={{ color: '#94a3b8', fontSize: '13px' }}>No experience listed</span>;
        if (typeof expObj === 'string') return <p style={{ fontSize: '13px', color: '#334155', margin: 0 }}>{expObj}</p>;
        if (typeof expObj === 'object' && !Array.isArray(expObj)) {
            const entries = Object.entries(expObj);
            if (entries.length === 0) return <span style={{ color: '#94a3b8', fontSize: '13px' }}>No experience listed</span>;
            return (
                <div style={{ fontSize: '13px' }}>
                    {entries.map(([k, v], idx) => (
                        <div key={idx} style={{ marginBottom: '4px' }}>
                            <strong style={{ color: '#0f172a' }}>{k}:</strong> {String(v)}
                        </div>
                    ))}
                </div>
            );
        }
        if (Array.isArray(expObj)) {
            return (
                <div style={{ fontSize: '13px' }}>
                    {expObj.map((e, idx) => (
                        <div key={idx} style={{ marginBottom: '4px' }}>
                            {typeof e === 'object' ? `${e.role || e.title || ''} (${e.duration || e.years || ''})` : String(e)}
                        </div>
                    ))}
                </div>
            );
        }
        return <p style={{ fontSize: '13px', margin: 0 }}>{String(expObj)}</p>;
    };

    if (loading) {
        return (
            <div className="page-premium-wrapper">
                <div className="page-premium-content text-center py-5 text-secondary">
                    Loading Hiremate candidate profile...
                </div>
            </div>
        );
    }

    if (!candidate) {
        return (
            <div className="page-premium-wrapper">
                <div className="page-premium-content text-center py-5">
                    <p style={{ color: '#ef4444', fontWeight: '700' }}>Candidate Profile Not Found</p>
                    <button
                        onClick={() => navigate('/hiremates')}
                        style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: '#334155', color: '#fff', border: 'none', cursor: 'pointer' }}
                    >
                        Back to Candidates List
                    </button>
                </div>
            </div>
        );
    }

    const user = candidate.Provider || {};
    const roleScores = parseJSON(candidate.role_scores, {});

    return (
        <div className="page-premium-wrapper hiremate-candidate-view-page">
            <div className="page-premium-content">

                {/* Back Button */}
                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <button
                        onClick={() => navigate('/hiremates')}
                        style={{
                            backgroundColor: '#ffffff',
                            color: '#334155',
                            border: '1px solid #cbd5e1',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                    >
                        <FaArrowLeft /> Back to Candidates List
                    </button>

                    <button
                        onClick={() => navigate(`/hiremates/${candidate.id}/requests`)}
                        style={{
                            backgroundColor: '#4f46e5',
                            color: '#ffffff',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '700',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)'
                        }}
                    >
                        <FaList /> View Job Invites & Requests
                    </button>
                </div>

                {/* Hero Profile Banner */}
                <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '20px',
                    padding: '24px 28px',
                    marginBottom: '24px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '20px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                        {candidate.profile_photo || user.avatar ? (
                            <img
                                src={candidate.profile_photo || user.avatar}
                                alt={user.name}
                                style={{ width: '72px', height: '72px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #0ab68e' }}
                            />
                        ) : (
                            <div style={{ width: '72px', height: '72px', borderRadius: '50%', backgroundColor: '#0ab68e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '28px' }}>
                                {user.name ? user.name.charAt(0).toUpperCase() : 'H'}
                            </div>
                        )}
                        <div>
                            <h2 style={{ margin: 0, fontWeight: '800', color: '#0f172a', fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {user.name || 'Hiremate Candidate'}
                                <FaCheckCircle style={{ color: '#0ab68e', fontSize: '18px' }} />
                            </h2>
                            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                <span><FaEnvelope style={{ color: '#94a3b8' }} /> {user.email || '-'}</span>
                                <span><FaPhone style={{ color: '#94a3b8' }} /> {user.phone || '-'}</span>
                            </p>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                        <span style={{
                            backgroundColor: candidate.overall_score >= 80 ? '#dcfce7' : '#fef9c3',
                            color: candidate.overall_score >= 80 ? '#15803d' : '#a16207',
                            padding: '10px 18px',
                            borderRadius: '16px',
                            fontWeight: '800',
                            fontSize: '15px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <FaStar /> Overall Score: {candidate.overall_score || 0}%
                        </span>

                        <span style={{
                            backgroundColor: candidate.available_now ? '#dcfce7' : '#f1f5f9',
                            color: candidate.available_now ? '#16a34a' : '#475569',
                            padding: '10px 18px',
                            borderRadius: '16px',
                            fontWeight: '700',
                            fontSize: '14px'
                        }}>
                            {candidate.available_now ? '⚡ Available Now' : (candidate.availability || 'Flexible Schedule')}
                        </span>
                    </div>
                </div>

                {/* Main Details Grid */}
                <div className="admin-2col-grid" style={{ marginBottom: '24px' }}>

                    {/* Categories & Roles */}
                    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaUserTie style={{ color: '#0ab68e' }} /> Target Categories & Roles
                        </h5>
                        <div style={{ marginBottom: '14px' }}>
                            <strong style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Selected Categories:</strong>
                            {renderBadges(candidate.categories, 'blue')}
                        </div>
                        <div>
                            <strong style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Target Roles:</strong>
                            {renderBadges(candidate.roles, 'purple')}
                        </div>
                    </div>

                    {/* Assessment Scores Breakdown */}
                    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaAward style={{ color: '#4f46e5' }} /> Skill Assessment Scores
                        </h5>
                        {typeof roleScores === 'object' && Object.keys(roleScores).length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {Object.entries(roleScores).map(([role, score], idx) => (
                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '10px' }}>
                                        <span style={{ color: '#334155', fontWeight: '600', fontSize: '14px' }}>{role}</span>
                                        <span style={{ color: '#0ab68e', fontWeight: '800', fontSize: '14px' }}>{score}%</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>No individual role assessment scores recorded.</p>
                        )}
                    </div>

                </div>

                {/* Work Experience & Skills */}
                <div className="admin-2col-grid" style={{ marginBottom: '24px' }}>

                    {/* Experience */}
                    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaBriefcase style={{ color: '#0ab68e' }} /> Work Experience
                        </h5>
                        {renderExperienceSummary(candidate.experience || candidate.experiences)}
                    </div>

                    {/* Skills */}
                    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaCheckCircle style={{ color: '#16a34a' }} /> Skills & Competencies
                        </h5>
                        {renderBadges(candidate.skills, 'green')}
                    </div>

                </div>

                {/* Availability & Account Details */}
                <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                    <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaCalendarAlt style={{ color: '#0ab68e' }} /> Availability & Account Info
                    </h5>
                    <div style={{ fontSize: '14px', color: '#334155', spaceY: '6px' }}>
                        <p style={{ margin: '4px 0' }}><strong>Immediate Status:</strong> {candidate.available_now ? '⚡ Available Now for Immediate On-Demand Shifts' : 'Not marked as available now'}</p>
                        <p style={{ margin: '4px 0' }}><strong>Preferred Hours / Days:</strong> {candidate.availability || 'Flexible Schedule'}</p>
                        <p style={{ margin: '4px 0' }}><strong>Registered Date:</strong> {candidate.createdAt ? new Date(candidate.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</p>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default HiremateCandidateProfileView;
